import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { extractErr } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import LoginPasswordInput, { passwordFromForm } from "@/components/LoginPasswordInput";
import FirebasePhoneBox from "@/components/FirebasePhoneBox";

export default function StorefrontCustomerLogin({ config }) {
  const { t } = useTranslation();
  const { base } = useStoreBase();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { login, loginPhone } = useCustomerAuth();
  const otpOn = Boolean(config?.firebase_otp_active && config?.firebase_otp?.apiKey);
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const afterLogin = () => {
    const redirect = (params.get("redirect") || "").trim();
    if (redirect === "checkout") return `${base}/checkout`;
    if (redirect.startsWith("/") && !redirect.startsWith("//")) return `${base}${redirect}`;
    return `${base}/`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const password = passwordFromForm(e.currentTarget);
    setLoading(true);
    try { await login(email, password); toast.success(t("store.welcomeBack")); nav(afterLogin()); }
    catch (e) { toast.error(extractErr(e)); }
    finally { setLoading(false); }
  };

  const phoneLogin = async ({ token }) => {
    setLoading(true);
    try { await loginPhone(token); toast.success(t("store.welcomeBack")); nav(afterLogin()); }
    catch (e) { toast.error(extractErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900">{otpOn && !emailMode ? "Login with mobile" : t("store.signIn")}</h1>
      <p className="text-stone-500 text-sm mt-1">{t("store.signInTo", { name: config.store_info?.name })}</p>
      {otpOn && !emailMode ? (
        <div className="mt-6 sm:mt-8 space-y-4">
          <FirebasePhoneBox config={config.firebase_otp} onVerified={phoneLogin} testPrefix="clogin-otp" />
          <button type="button" className="w-full text-sm text-stone-700 underline underline-offset-2" data-testid="clogin-use-email" onClick={() => setEmailMode(true)}>
            Login with email
          </button>
        </div>
      ) : (
      <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-4">
        <div><Label>{t("common.email")}</Label><Input required type="email" data-testid="clogin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" /></div>
        <div><Label>{t("common.password")}</Label><LoginPasswordInput testId="clogin-password" className="mt-1" /></div>
        <div className="text-right">
          <Link to={`${base}/account/forgot-password`} className="text-sm text-stone-700 underline" data-testid="clogin-forgot">Forgot password?</Link>
        </div>
        <Button type="submit" disabled={loading} data-testid="clogin-submit" className="w-full h-11 btn-store-primary rounded-full">{loading ? t("store.signingIn") : t("store.signIn")}</Button>
        {otpOn ? (
          <button type="button" className="w-full text-sm text-stone-700 underline underline-offset-2" data-testid="clogin-use-mobile" onClick={() => setEmailMode(false)}>
            Login with mobile
          </button>
        ) : null}
      </form>
      )}
      <div className="mt-6 text-sm text-stone-500 text-center">{t("store.newHere")} <Link to={`${base}/account/signup`} className="text-stone-900 underline" data-testid="clogin-to-signup">{t("store.createAccount")}</Link></div>
    </div>
  );
}
